import { useNavigate, useParams } from "react-router-dom";
import HotspotInfo from "../components/wall/hotspotGridItem/HotspotInfo";
import useWallStore from "../store/useWallStore";
import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, Users } from "lucide-react";
import { WallCardImageLinearGradient } from "../constants/gradientDefinitions";
import { ClimbingBoxLoader } from "react-spinners";
import { trackScreen, trackEvent } from "../utils/mixpanel";
import ImagePreviewModal from "../components/ImagePreviewModal";
import HotspotIntentMarker from "../components/wall/hotspotGridItem/HotspotIntentMarker";

const HotspotDetail = () => {
  const { id } = useParams();
  const getHotspotbyId = useWallStore((state) => state.getHotspotById);

  const [hotspot, setHotspots] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const pageStartTime = useRef(Date.now());
  const galleryImagesViewed = useRef(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        const hotspot = await getHotspotbyId(id);
        setHotspots(hotspot);
        setIsLoading(true);
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Track page opened when hotspot data is available
  useEffect(() => {
    if (hotspot) {
      trackScreen("wall_hotspot_page_opened", {
        hotspot_id: hotspot.originalId,
        hotspot_name: hotspot.title,
        host_name: hotspot.host?.name,
        experience_type: hotspot.experience,
      });
    }
  }, [hotspot]);

  const handleBackBtnClick = () => {
    const timeSpent = Date.now() - pageStartTime.current;
    trackEvent("wall_back_button_clicked", {
      hotspot_id: hotspot?.originalId,
      time_spent_ms: timeSpent,
    });
    trackEvent("wall_hotspot_engagement_time", {
      hotspot_id: hotspot?.originalId,
      time_spent_ms: timeSpent,
      gallery_images_viewed: galleryImagesViewed.current,
    });
    navigate(-1);
  };

  const handleHotspotNameClick = () => {
    if (hotspot?.originalId && window.flutter_inappwebview) {
      const deeplink = `/public-hotspot/${hotspot.originalId}?shareCode=${hotspot.shareCode}`;
      window.flutter_inappwebview.callHandler("navigate", deeplink);
    } else {
      console.error("Cannot navigate - originalId:", hotspot?.originalId);
    }
  };

  const handleHostClick = () => {
    if (hotspot?.host?.id && window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler(
        "navigate",
        `/public-profile/${hotspot.host.id}`,
      );
    }
  };

  const handleImageClick = (index) => {
    trackEvent("wall_gallery_image_clicked", {
      hotspot_id: hotspot?.originalId,
      image_index: index,
      image_url: hotspot?.galleryImgUrls?.[index],
      total_images: hotspot?.galleryImgUrls?.length,
    });
    setCurrentImageIndex(index);
    setIsPreviewOpen(true);
    galleryImagesViewed.current += 1;
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };

  const handleNavigateImage = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  if (isLoading || !hotspot) {
    return (
      <div className="center-container">
        <ClimbingBoxLoader color="#5961FF" size={16} />
      </div>
    );
  }

  return (
    <>
      <motion.div initial={{}} animate={{}} className="hotspot-info-header">
        <ChevronLeft
          size={28}
          className="back-btn"
          onClick={handleBackBtnClick}
        />
        <p>{hotspot.experience}</p>
        {/* <p>|</p>
        <Users size={14} />
        <p>{hotspot.inviteeCount}</p> */}
      </motion.div>
      <motion.div
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="hotspot-grid-item active intersecting"
        style={{
          backgroundImage: `${WallCardImageLinearGradient}, url("${hotspot.bannerImgUrl}")`,
          borderRadius: "8px",
        }}
      >
        <HotspotInfo
          title={hotspot.title}
          venue={hotspot.venue}
          date={hotspot.date}
          titleFont={hotspot.font}
          hotspotId={hotspot.originalId}
          onTitleClick={handleHotspotNameClick}
        />
      </motion.div>
      {hotspot.shareCode && (
        <motion.div
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
        >
          {/* Intent Marker Card */}
          <HotspotIntentMarker hotspotId={hotspot.originalId} />
        </motion.div>
      )}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ type: "spring", duration: 1, delay: 0.5 }}
        className="grid-item-hotspot-preview"
      >
        <div
          className="host-details"
          onClick={handleHostClick}
          style={{ cursor: "pointer" }}
        >
          <span>Hosted By</span>
          <p>{hotspot.host.name}</p>
          {hotspot.host.followerCount && (
            <span>{hotspot.host.followerCount} followers</span>
          )}
        </div>
        <img
          src={hotspot.host.profileImageUrl}
          alt="host-img"
          onClick={handleHostClick}
          style={{ cursor: "pointer" }}
        />
        {hotspot.galleryImgUrls.map((image, index) => (
          <img
            key={index}
            src={image}
            alt={`Hotspot ${index}`}
            className={(index + 1) % 3 === 0 ? "large" : "small"}
            loading="lazy"
            onClick={() => handleImageClick(index)}
          />
        ))}
      </motion.div>

      <ImagePreviewModal
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
        images={hotspot.galleryImgUrls}
        currentIndex={currentImageIndex}
        onNavigate={handleNavigateImage}
        hotspotId={hotspot.originalId}
      />
    </>
  );
};

export default HotspotDetail;
